import type { ReactNode } from "react";
import { BreadcrumbCurrentContext } from "@/components/ui/page-breadcrumb";
import { cn } from "@/lib/utils";

// Keep existing detail pages on the same breadcrumb primitive as other pages.
export {
	BREADCRUMB_LINK_CLASS as DETAIL_CRUMB_LINK_CLASS,
	PageBreadcrumb as DetailBreadcrumb,
} from "@/components/ui/page-breadcrumb";

/**
 * Connector / connection / feed header.
 *
 *   Connectors › GitHub › Old fork            ← trail, own line, one size down
 *   [icon]  Old fork  [error]        [meta]   ← icon + title row, centered
 *   description under the whole block
 */
export function DetailHeader({
	breadcrumb,
	icon,
	title,
	badge,
	status,
	meta,
	caption,
	captionTone = "muted",
}: {
	breadcrumb?: ReactNode;
	icon?: ReactNode;
	title: ReactNode;
	badge?: ReactNode;
	status?: ReactNode;
	meta?: ReactNode;
	caption?: ReactNode;
	captionTone?: "muted" | "error";
}) {
	const titleRow = (
		<div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-1">
			<div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
				{typeof title === "string" ? (
					<h1 className="text-2xl font-semibold leading-none tracking-tight">
						{title}
					</h1>
				) : (
					title
				)}
				{badge}
				{status}
			</div>
			{meta ? (
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
					{meta}
				</div>
			) : null}
		</div>
	);

	const captionEl = caption ? (
		<p
			className={cn(
				"max-w-2xl text-base leading-relaxed",
				captionTone === "error" ? "text-destructive" : "text-muted-foreground",
			)}
		>
			{caption}
		</p>
	) : null;

	return (
		<div className="flex flex-col gap-2">
			<BreadcrumbCurrentContext.Provider
				value={typeof title === "string" ? title : null}
			>
				{breadcrumb}
			</BreadcrumbCurrentContext.Provider>
			{icon ? (
				// Icon + title on one row, vertically centered.
				<div className="flex min-w-0 items-center gap-3">
					<div className="shrink-0">{icon}</div>
					{titleRow}
				</div>
			) : (
				titleRow
			)}
			{captionEl}
		</div>
	);
}
